import { useState } from 'react'
import { Organization } from '../api'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Building2, Users, Settings } from 'lucide-react'
import { OrganizationUsersTab } from './OrganizationUsersTab'

interface OrganizationDetailTabsProps {
  organization: Organization
}

export function OrganizationDetailTabs({ organization }: OrganizationDetailTabsProps) {
  const [activeTab, setActiveTab] = useState('details')

  const isDefaultOrg = organization.name === 'default'
  const isDeleted = organization.isDeleted

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{organization.displayName}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{organization.name}</span>
                  {organization.userCount !== undefined && (
                    <span className="text-sm text-muted-foreground">
                      ({organization.userCount} users)
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={organization.isActive ? 'default' : 'secondary'}>
                {organization.isActive ? 'Active' : 'Inactive'}
              </Badge>
              {isDefaultOrg && (
                <Badge variant="outline">Default</Badge>
              )}
              {isDeleted && (
                <Badge variant="destructive">Deleted</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
              {organization.userCount !== undefined && (
                <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 text-xs">
                  {organization.userCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Name</h4>
                <p className="font-mono text-sm">{organization.name}</p>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Display Name</h4>
                <p className="text-sm">{organization.displayName}</p>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Domain</h4>
                <p className="text-sm">
                  {organization.domain ? (
                    <span className="font-mono">{organization.domain}</span>
                  ) : (
                    <span className="text-muted-foreground">No domain set</span>
                  )}
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Status</h4>
                <Badge variant={organization.isActive ? 'default' : 'secondary'}>
                  {organization.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Created</h4>
                <p className="text-sm">
                  {new Date(organization.createdAt).toLocaleString()}
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Last Updated</h4>
                <p className="text-sm">
                  {new Date(organization.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="users" className="space-y-4">
            <OrganizationUsersTab 
              organization={organization} 
              disabled={isDeleted}
            />
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <div className="text-center py-8">
              <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Settings</h3>
              <p className="text-muted-foreground">
                Organization settings will be available in a future update.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}
